import { Council } from "@abaco/crop-plan-module/src/modules/crop-plan-module/models/Council";
import { HTTPClient, propsToDate, propsToDatePaged } from "@abaco/web-common/src/HTTP";
import { dbGIStoStandardPage, PagedResults, PagingParams } from "@abaco/web-common/src/Paging";
import {
    Alarm,
    AlarmGroup,
    AlarmWidget,
    AlarmFilter,
    AlarmFilterDetail,
    AlarmFilterOut,
    AlarmThreshold,
    AlarmThresholdDetail,
    ThresholdDefault,
    AlarmFilterNotificationOut,
    SeedVariety,
    CropMatrixClass,
    CropMatrixClassCrop,
} from "../models/Alarm";

export default class GrapesApi {
    private http: HTTPClient;

    constructor(http: HTTPClient) {
        this.http = http;
    }

    public async getGroups(): Promise<PagedResults<AlarmGroup>> {
        const res = await this.http.get(`/sitiagri-rest-api/api_basic/v1/dss/dictionary_groups`, {
            params: {
                filter: "has_alarms eq 1",
                offset: 0,
                count: 300,
            },
        });
        return res.data;
    }

    public async getWidgets(pkCuaa: number, groupCode: string | null): Promise<PagedResults<AlarmWidget>> {
        const filter = [];
        filter.push("has_alarms eq 1");
        if (groupCode && groupCode != "") {
            filter.push(`group_code eq ${groupCode}`);
        }
        const res = await this.http.get(
            `/sitiagri-rest-api/api_basic/v1/dss/subjects/${pkCuaa}/widgets?sort=+group_descr&sort=+widget_descr`,
            {
                params: {
                    filter: filter.length ? filter : undefined,
                    offset: 0,
                    count: 300,
                },
            }
        );
        return res.data;
    }

    public async getSubjectCouncils(pkCuaa: number): Promise<PagedResults<Council>> {
        const res = await this.http.get(
            `/sitiagri-rest-api/api_basic/v1/plots/subjects/${pkCuaa}/dictionary_councils`,
            {
                params: {
                    offset: 0,
                    count: 300,
                },
            }
        );
        return res.data;
    }

    public async getSubjectVarieties(pkCuaa: number, campaign: number): Promise<SeedVariety[]> {
        const res = await this.http.get(
            `/sitiagri-rest-api/api_basic/v1/dashboard/subjects/${pkCuaa}/campaigns/${campaign}/varieties_v2`,
            {
                params: {
                    offset: 0,
                    count: 300,
                },
            }
        );
        return res.data && res.data.varieties ? res.data.varieties : [];
    }

    public async getSubjectSeedVarieties(
        pk_cuaa: number,
        paging: PagingParams,
        filter: string | null
    ): Promise<PagedResults<SeedVariety>> {
        const res = await this.http.get(`/sitiagri-rest-api/api_basic/v1/warehouse/subjects/${pk_cuaa}/seeds`, {
            params: {
                offset: paging.offset,
                count: paging.count,
                filter: filter ? `^item_name like ${filter.toUpperCase()}` : undefined,
            },
        });

        return res.data;
    }

    public async getCropMatrixClasses(code: string): Promise<CropMatrixClass[]> {
        const res = await this.http.get(`/sitiagri-rest-api/api_basic/v1/land_uses/dictionary_matrix/${code}/classes`, {
            params: {
                offset: 0,
                count: 300,
            },
        });
        return res.data.records;
    }

    public async getCropMatrixClassCrops(matrix: string, code: string | number): Promise<CropMatrixClassCrop[]> {
        const res = await this.http.get(
            `/sitiagri-rest-api/api_basic/v1/land_uses/dictionary_matrix/${matrix}/classes/${code}`,
            {
                params: {
                    offset: 0,
                    count: 300,
                },
            }
        );
        return res.data;
    }

    public async getAlarmsPerWidget(pkCuaa: number, widgetCode: string): Promise<PagedResults<Alarm>> {
        const res = await this.http.get(
            `/sitiagri-rest-api/api_basic/v1/dss/subjects/${pkCuaa}/widgets/${widgetCode}/alarms`,
            {
                params: {
                    offset: 0,
                    count: 300,
                },
            }
        );
        return res.data;
    }

    public async getAlarmThresholds(widgetCode: string, alarmCode: string): Promise<PagedResults<AlarmThreshold>> {
        const res = await this.http.get(
            `/sitiagri-rest-api/api_basic/v1/dss/widgets/${widgetCode}/alarms/${alarmCode}/dictionary_thresholds`,
            {
                params: {
                    offset: 0,
                    count: 300,
                },
            }
        );
        return res.data;
    }

    public async getFarmerLeaderCheck(subjectId: number): Promise<boolean> {
        const res = await this.http.get(
            `/sitiagri-rest-api/api_basic/v1/supply_chain/leaders?filter=subject_id eq ${subjectId}`
        );
        return res.data && res.data.count && res.data.count > 0 ? true : false;
    }

    public async getThresholdsDefaults(
        pkCuaa: number,
        widgetCode: string,
        alarmCode: string,
        varId: number,
        councilCode: number | string | null,
        plotId: number | string | null,
        blockId: number | string | null,
        varietyId: number | string | null
    ): Promise<ThresholdDefault[]> {
        let filter = `subject_id=${pkCuaa}`;
        if (councilCode && councilCode != "") {
            filter += `&council_code=${councilCode}`;
        }
        if (plotId && plotId != "") {
            filter += `&plot_id=${plotId}`;
        }
        if (blockId && blockId != "") {
            filter += `&block_id=${blockId}`;
        }
        if (varietyId && varietyId != "") {
            filter += `&item_id=${varietyId}`;
        }
        const res = await this.http.get(
            `/sitiagri-rest-api/api_basic/v1/dss/widgets/${widgetCode}/alarms/${alarmCode}/dictionary_thresholds/${varId}/prepops?${filter}`,
            {
                params: {
                    offset: 0,
                    count: 300,
                },
            }
        );
        return res.data;
    }

    public async getFiltersPerAlarm(
        pkCuaa: number,
        widgetCode: string,
        alarmCode: string
    ): Promise<PagedResults<AlarmFilter>> {
        const res = await this.http.get(
            `/sitiagri-rest-api/api_basic/v1/dss/subjects/${pkCuaa}/widgets/${widgetCode}/alarms/${alarmCode}/filters`,
            {
                params: {
                    offset: 0,
                    count: 300,
                },
            }
        );
        return res.data;
    }

    public async getAlarmFilterDetail(
        pkCuaa: number,
        widgetCode: string,
        alarmCode: string,
        filterId: number
    ): Promise<AlarmFilterDetail> {
        const res = await this.http.get(
            `/sitiagri-rest-api/api_basic/v1/dss/subjects/${pkCuaa}/widgets/${widgetCode}/alarms/${alarmCode}/filters/${filterId}`
        );
        return res.data;
    }

    public async getAlarmFilterDetailThresholds(
        pkCuaa: number,
        widgetCode: string,
        alarmCode: string,
        filterId: number
    ): Promise<PagedResults<AlarmThresholdDetail>> {
        const res = await this.http.get(
            `/sitiagri-rest-api/api_basic/v1/dss/subjects/${pkCuaa}/widgets/${widgetCode}/alarms/${alarmCode}/thresholds/${filterId}`
        );
        return res.data;
    }

    public async insertAlarmFilterDetail(
        pkCuaa: number,
        widgetCode: string,
        alarmCode: string,
        form: AlarmFilterOut
    ): Promise<AlarmFilterDetail> {
        const res = await this.http.post(
            `/sitiagri-rest-api/api_basic/v1/dss/subjects/${pkCuaa}/widgets/${widgetCode}/alarms/${alarmCode}/filters`,
            form
        );
        return res.data;
    }
    public async updateAlarmFilterDetail(
        pkCuaa: number,
        widgetCode: string,
        alarmCode: string,
        filterId: number,
        form: AlarmFilterOut
    ): Promise<AlarmFilterDetail> {
        const res = await this.http.put(
            `/sitiagri-rest-api/api_basic/v1/dss/subjects/${pkCuaa}/widgets/${widgetCode}/alarms/${alarmCode}/filters/${filterId}`,
            form
        );
        return res.data;
    }
    public async toggleAlarmFilterDetailNotification(
        pkCuaa: number,
        widgetCode: string,
        alarmCode: string,
        filterId: number,
        form: AlarmFilterNotificationOut
    ): Promise<AlarmFilterDetail> {
        const res = await this.http.put(
            `/sitiagri-rest-api/api_basic/v1/dss/subjects/${pkCuaa}/widgets/${widgetCode}/alarms/${alarmCode}/filters/${filterId}`,
            form
        );
        return res.data;
    }

    public async deleteAlarmFilter(
        pkCuaa: number,
        widgetCode: string,
        alarmCode: string,
        filterId: number
    ): Promise<any> {
        const res = await this.http.delete(
            `/sitiagri-rest-api/api_basic/v1/dss/subjects/${pkCuaa}/widgets/${widgetCode}/alarms/${alarmCode}/filters/${filterId}`
        );
        return res.data;
    }
}
